import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TimePicker } from "@/components/ui/time-picker";
import { Tag, Trash2, X } from "lucide-react";
import { formatDuration } from "@/lib/api";
import { ClientMultiSelect, TagSingleSelect, MetaSelect } from "./pickers";
import { toTimeStr, setTime } from "./utils";

function seedClientIds(e) {
  return Array.isArray(e?.client_ids) && e.client_ids.length
    ? e.client_ids
    : (e?.client_id ? [e.client_id] : []);
}

/**
 * Modal editor for creating or editing a single time entry (or leave entry).
 * Mounted by WeekCalendar when the user drags-to-create or clicks an entry.
 */
export default function EntryEditor({
  open, onClose, entry, draft, clients, activities, tags,
  onSave, onDelete, onCreateClient, onCreateActivity,
}) {
  const initial = entry || draft;

  const [description, setDescription] = useState(initial?.description || "");
  const [clientIds, setClientIds] = useState(seedClientIds(initial));
  const [activityId, setActivityId] = useState(initial?.activity_id || null);
  const [startStr, setStartStr] = useState(initial ? toTimeStr(new Date(initial.start)) : "09:00");
  const [endStr, setEndStr] = useState(initial && initial.end ? toTimeStr(new Date(initial.end)) : "10:00");
  const [leaveType, setLeaveType] = useState(initial?.leave_type || null);
  const [tagIds, setTagIds] = useState(initial?.tag_ids || []);
  const [autoActivityId, setAutoActivityId] = useState(initial?.activity_id || null);

  useEffect(() => {
    if (!open) return;
    const e = entry || draft;
    if (!e) return;
    setDescription(e.description || "");
    setClientIds(seedClientIds(e));
    setActivityId(e.activity_id || null);
    setStartStr(toTimeStr(new Date(e.start)));
    setEndStr(e.end ? toTimeStr(new Date(e.end)) : toTimeStr(new Date(new Date(e.start).getTime() + 60 * 60 * 1000)));
    setLeaveType(e.leave_type || null);
    setTagIds(e.tag_ids || []);
    setAutoActivityId(e.activity_id || null);
  }, [open, entry, draft]);

  // Auto-suggest category when the user changes the activity.
  useEffect(() => {
    if (!open) return;
    if (activityId === autoActivityId) return;
    const auto = (tags || [])
      .filter((t) => Array.isArray(t.activity_ids) && t.activity_ids.includes(activityId))
      .map((t) => t.id);
    setTagIds(auto.length ? [auto[0]] : []);
    setAutoActivityId(activityId);
  }, [activityId, autoActivityId, open, tags]);

  if (!open || !initial) return null;

  const date = new Date(initial.start);
  const startDt = setTime(date, startStr);
  const endDt = setTime(date, endStr);
  const duration = Math.max(0, Math.floor((endDt - startDt) / 1000));
  const isLeave = !!leaveType;

  const submit = () => {
    if (endDt <= startDt) return;
    onSave({
      ...(entry || {}),
      description,
      client_ids: isLeave ? [] : clientIds,
      activity_id: isLeave ? null : activityId,
      tag_ids: isLeave ? [] : tagIds,
      leave_type: leaveType || null,
      start: startDt.toISOString(),
      end: endDt.toISOString(),
    });
  };

  return (
    <div className="fixed inset-0 z-40" onClick={onClose}>
      <div className="absolute inset-0 bg-slate-900/10" />
      <div
        className="absolute left-1/2 top-24 -translate-x-1/2 bg-white border border-slate-200 rounded-xl shadow-xl w-[420px] fade-in-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="entry-editor"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-medium text-slate-700" style={{ fontFamily: "Manrope" }}>
            {entry ? "Edit time entry" : "Add time entry"}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="entry-editor-close">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-4 space-y-3">
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isLeave ? "Add a note (optional)" : "Add a description"}
            data-testid="entry-description-input"
          />
          <div className={isLeave ? "opacity-40 pointer-events-none" : ""}>
            <ClientMultiSelect clients={clients} value={clientIds} onChange={setClientIds} onCreate={onCreateClient} />
            {!isLeave && clientIds.length === 0 && (
              <div className="text-[11px] text-amber-600 mt-1" data-testid="entry-client-required">
                At least one client is required.
              </div>
            )}
          </div>
          <div className={`grid grid-cols-2 gap-2 ${isLeave ? "opacity-40 pointer-events-none" : ""}`}>
            <TagSingleSelect tags={tags || []} value={tagIds[0] || null} onChange={(id) => setTagIds(id ? [id] : [])} required={!isLeave} />
            <MetaSelect items={activities} value={activityId} onChange={setActivityId} onCreate={onCreateActivity}
                        defaultColor="#00b5cc" icon={Tag} placeholder="Activity (optional)" testId="entry-activity-picker" />
          </div>
          <div className="flex items-center gap-2">
            <TimePicker value={startStr} onChange={setStartStr}
                        className="w-28" testId="entry-start-input" />
            <span className="text-slate-400">→</span>
            <TimePicker value={endStr} onChange={setEndStr}
                        className="w-28" testId="entry-end-input" />
            <div className="ml-auto timer-font text-sm text-slate-600" data-testid="entry-duration">
              {formatDuration(duration)}
            </div>
          </div>
          <div className="text-xs text-slate-400">
            {date.toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" })}
          </div>
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100 -mx-4 px-4 pt-3">
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={isLeave}
                onChange={(e) => setLeaveType(e.target.checked ? (leaveType || "annual") : null)}
                data-testid="entry-mark-leave-checkbox"
                className="h-4 w-4 accent-[#145c8c]"
              />
              Mark as Leave
            </label>
            {isLeave && (
              <select
                value={leaveType || "annual"}
                onChange={(e) => setLeaveType(e.target.value)}
                data-testid="entry-leave-type-select"
                className="ml-auto h-8 text-sm border border-slate-200 rounded-md px-2 bg-white focus:border-[#145c8c] outline-none"
              >
                <option value="annual">Annual Leave</option>
                <option value="sick">Sick Leave</option>
                <option value="personal">Personal Leave</option>
              </select>
            )}
          </div>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between">
          {entry ? (
            <button
              className="text-red-600 hover:bg-red-50 px-2 py-1 rounded-md text-xs flex items-center gap-1"
              onClick={() => onDelete(entry)}
              data-testid="entry-delete-button"
            >
              <Trash2 className="h-3.5 w-3.5" /> Delete
            </button>
          ) : <div />}
          <Button onClick={submit} className="text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: "#145c8c" }}
                  disabled={!isLeave && (tagIds.length === 0 || clientIds.length === 0)}
                  title={
                    !isLeave && clientIds.length === 0 ? "Pick a client before saving"
                      : !isLeave && tagIds.length === 0 ? "Pick a category before saving"
                      : ""
                  }
                  data-testid="entry-save-button">
            {entry ? "Save" : "Add"}
          </Button>
        </div>
      </div>
    </div>
  );
}
